import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import EmergencyChecklistCard from '../EmergencyChecklistCard';
import { emergencyChecklists } from '@/lib/mockModulesData';
import { toast } from 'sonner';

export const EmergencyModule = ({ weddingId }) => {
  const [checklists, setChecklists] = useState(emergencyChecklists);
  const [criticalMode, setCriticalMode] = useState(false);

  // Calculate stats
  const activeChecklists = checklists.filter(c => c.isActive).length;
  const totalTasks = checklists.reduce((sum, c) => sum + c.checklist.length, 0);
  const completedTasks = checklists.reduce((sum, c) => 
    sum + c.checklist.filter(t => t.completed).length, 0
  );
  const completionRate = totalTasks > 0 
    ? Math.round((completedTasks / totalTasks) * 100) 
    : 0;

  const handleToggleCriticalMode = () => {
    setCriticalMode(!criticalMode);
    if (!criticalMode) {
      toast.error('🚨 Critical Mode Activated! All emergency protocols are now visible.');
    } else {
      toast.success('Critical Mode Deactivated');
    }
  };

  const handleActivateChecklist = (id) => {
    setChecklists(checklists.map(c => 
      c.id === id 
        ? { ...c, isActive: true, activatedAt: new Date().toISOString() }
        : c
    ));
    const checklist = checklists.find(c => c.id === id);
    toast.error(`🚨 ${checklist.type} - Emergency Protocol Activated!`);
  };

  const handleDeactivateChecklist = (id) => {
    setChecklists(checklists.map(c => 
      c.id === id 
        ? { ...c, isActive: false, activatedAt: null }
        : c
    ));
    toast.success('Emergency protocol deactivated');
  };

  const handleToggleTask = (checklistId, taskId) => {
    setChecklists(checklists.map(c => 
      c.id === checklistId 
        ? {
            ...c,
            checklist: c.checklist.map(t =>
              t.id === taskId ? { ...t, completed: !t.completed } : t
            )
          }
        : c
    ));
  };

  const activeChecklistsData = checklists.filter(c => c.isActive);
  const availableChecklistsData = checklists.filter(c => !c.isActive);

  return (
    <div>
      {/* Critical Mode Alert */}
      {criticalMode && (
        <Alert className="mb-6 border-destructive bg-destructive/10">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-destructive font-semibold">
            ⚠️ CRITICAL MODE ACTIVE - Emergency protocols visible. Handle with care.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-t-4 border-t-primary">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{checklists.length}</p>
              <p className="text-xs text-muted-foreground">Total Protocols</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-destructive">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">{activeChecklists}</p>
              <p className="text-xs text-muted-foreground">Active Emergencies</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-warning">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-warning">{completedTasks}/{totalTasks}</p>
              <p className="text-xs text-muted-foreground">Tasks Complete</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-success">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-success">{completionRate}%</p>
              <p className="text-xs text-muted-foreground">Resolution Rate</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Critical Mode Toggle */}
      <Card className="p-4 mb-6 border-2 border-destructive/50 bg-destructive/5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Critical Mode
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Enable to access all emergency protocols and activate checklists
            </p>
          </div>
          <Switch
            checked={criticalMode}
            onCheckedChange={handleToggleCriticalMode}
            className="data-[state=checked]:bg-destructive"
          />
        </div>
      </Card>

      {/* Active Emergencies */}
      {activeChecklistsData.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h2 className="text-xl font-bold">Active Emergencies</h2>
            <Badge variant="destructive">{activeChecklistsData.length}</Badge>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activeChecklistsData.map((checklist) => (
              <EmergencyChecklistCard
                key={checklist.id}
                checklist={checklist}
                onDeactivate={handleDeactivateChecklist}
                onToggleTask={handleToggleTask}
                criticalMode={criticalMode}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Emergency Protocols */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-xl font-bold">Available Emergency Protocols</h2>
        </div>

        {!criticalMode ? (
          <Card className="p-12 text-center border-2 border-dashed">
            <Shield className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Emergency Protocols Locked</h3>
            <p className="text-muted-foreground mb-6">
              Enable Critical Mode above to access and activate emergency checklists
            </p>
            <Button 
              onClick={handleToggleCriticalMode} 
              variant="destructive"
              className="gap-2"
            >
              <AlertTriangle className="h-4 w-4" />
              Enable Critical Mode
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {availableChecklistsData.map((checklist) => (
              <EmergencyChecklistCard
                key={checklist.id}
                checklist={checklist}
                onActivate={handleActivateChecklist}
                onToggleTask={handleToggleTask}
                criticalMode={criticalMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyModule;
